import React, {FC, PropsWithChildren, useCallback, useMemo, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControl,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import {ExpandMore} from '@material-ui/icons';
import {Autocomplete} from '@material-ui/lab';
import moment from 'moment';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';
import DateFnsUtils from '@date-io/date-fns';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {AGENIUM_OCUPATION_LIST, STATE_OPTIONS} from '../../constants';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/PrequalXref';
import InputSsn from '../../../UI/EmcApplicationQuestions/inputs/core/InputSsn';
import FixedNavigation from './common/FixedNavigation';

interface ReviewProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    formType: 'single-quote' | 'combination-quote' | undefined;
    quoteResponse: any[];
    onExit: () => void;
    onNext: () => void;
}
const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#456d69',
        marginBottom: '28px',
    },
    subTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    formSection: {
        width: '100%',
        marginBottom: 24,
    },
    formSectionTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '30.72px',
        letterSpacing: '2%',
        color: '#434343',
        padding: '30px 0 20px',
    },
    formSectionTitleGrey: {
        color: '#939393',
        paddingBottom: 0,
    },
    text: {
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    formSwitch: {
        marginTop: -4,
        marginLeft: 0,
        paddingLeft: 0,
        '& .MuiIconButton-label': {
            borderRadius: 25,
        },
        '& .MuiIconButton-root': {
            marginLeft: -6,
        },
        '& .MuiSwitch-track': {
            height: 24,
            marginTop: -5,
            borderRadius: 12,
        },
        '& .Mui-checked': {
            color: '#fff',
        },
        '& .Mui-checked + .MuiSwitch-track': {
            opacity: 1,
        },
    },
    switchStatus: {
        display: 'inline-block',
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '18.92px',
        lineHeight: '50px',
        color: '#434343',
        marginBottom: 12,
    },
    smallText: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#838383',
        marginBottom: '18px',
    },
    label: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
    },
    ageField: {
        width: 50,
        '& .MuiInputBase-input': {
            textAlign: 'center',
        },
    },
    uncollectedAccordion: {
        width: '100%',
        boxShadow: 'none',
        '& .MuiAccordionSummary-root': {
            padding: 0,
        },
        '& .MuiAccordionDetails-root': {
            padding: '8px 0 60px',
        },
        '&:before': {
            backgroundColor: 'transparent',
        },
        '&.Mui-expanded': {
            margin: 0,
        },
    },
    navigation: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        marginTop: 0,
        marginBottom: 0,
        padding: '16px 20px',
        height: '100px',
        backgroundColor: '#fff',
        marginLeft: '16.666%',
        width: 'calc(100% - 16.666%)',
        boxShadow: '6px -4px 10px rgba(0, 0, 0, 0.08)',
        zIndex: 10,
    },
    nextBtn: {
        minWidth: '400px',
        padding: '16px 80px',
        backgroundColor: '#5ae288 !important',
        borderRadius: '50px',
        color: '#174943',
        fontSize: '18px',
        letterSpacing: '0.05em',
        fontFamily: 'FredokaRegular',
    },
}));

const Review: React.FC<ReviewProps> = observer(({onExit, onNext, parameterStore, contactFormStore}) => {
    const classes = useStyles();
    const {onValueChangedPreQual, prequal, approval} = parameterStore;
    const [isMissingValueField] = useState({
        firstName: !approval.firstName.value?.length,
        lastName: !approval.lastName.value?.length,
        phoneNumber: !approval.phoneNumber.value?.length,
        emailAddress: !approval.emailAddress.value?.length,
        dob: !prequal.byear.value?.length || !prequal.bmonth.value?.length || !prequal.bday.value?.length,
        gender: !prequal.gender.value?.length,
        state: !prequal.state.value?.length,
        maritalStatus: !prequal.maritalStatus.value?.length,
        address: !approval.address.value?.length,
        addressCity: !approval.addressCity.value?.length,
        addressZipCode: !approval.addressZipCode.value?.length,
        birthCountry: !approval.birthCountry.value?.length,
        birthState: !approval.birthState.value?.length,
        usCitizenship: !approval.usCitizenship.value?.length,
        validDriversLicense: !approval.validDriversLicense.value?.length,
        height: !prequal.height.value || !prequal.height.value?.length,
        weight: !prequal.weight.value || !prequal.weight.value?.length,
        socialSecurityNumber: !approval.socialSecurityNumber.value?.length,
        nameOfEmployer: !prequal.nameOfEmployer.value?.length,
        occupation: !AGENIUM_OCUPATION_LIST.includes(prequal.occupation.value),
        incomeHouseholdText: !prequal.incomeHouseholdText.value?.length,
        existingCoverageAmount: !prequal.existingCoverageAmount.value?.length,
    });
    const [ageValue, setAgeValue] = useState<null | number>(null);
    const FormSection: FC<PropsWithChildren<any>> = useCallback(
        ({title, greyout, children}: {title: string; greyout: boolean; children: any}) => (
            <Grid item xs={12} className={classes.formSection}>
                <Box className={`${classes.formSectionTitle} ${greyout ? classes.formSectionTitleGrey : ''}`}>
                    {title}
                </Box>
                {children}
            </Grid>
        ),
        [],
    );

    const calculateAge = (birthMonth: number, birthDay: number, birthYear: number) => {
        const today = new Date();
        const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) age--;
        return age;
    };

    const formattedDateOfBirth = useMemo(() => {
        const year = prequal.byear.value;
        const month = prequal.bmonth.value;
        const day = prequal.bday.value;
        const date = moment([year, month - 1, day]);
        setAgeValue(calculateAge(month, day, year));
        return date.format('yyyy/MM/DD');
    }, [prequal.byear.value, prequal.bmonth.value, prequal.bday.value]);

    const setDateOfBirth = (value: MaterialUiPickersDate) => {
        if (value) {
            onValueChangedPreQual(prequal.byear, value.getFullYear());
            onValueChangedPreQual(prequal.bmonth, value.getMonth() + 1);
            onValueChangedPreQual(prequal.bday, value.getDate());
        }
    };

    const handleHeightChange = (value: string) => {
        const splitHeight = value?.split('/');
        if (splitHeight.length !== 2) {
            return;
        }
        const ft = parseInt(splitHeight[0]?.trim());
        const inch = parseInt(splitHeight[1]?.trim());
        if (!ft || !inch) {
            return;
        } else onValueChangedPreQual(prequal.height, ft * 12 + inch);
    };

    function renderBasics(renderMissingValueFields = false) {
        return (
            <>
                {renderMissingValueFields === isMissingValueField.firstName && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Client's First Name</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Client's First Name"
                            disabled
                            defaultValue={approval.firstName.value}
                            onChange={(event) => onValueChangedPreQual(approval.firstName, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.lastName && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Client's Last Name</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Client's Last Name"
                            disabled
                            defaultValue={approval.lastName.value}
                            onChange={(event) => onValueChangedPreQual(approval.lastName, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.phoneNumber && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Phone Number</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Phone Number"
                            disabled
                            defaultValue={approval.phoneNumber.value}
                            onChange={(event) => onValueChangedPreQual(approval.phoneNumber, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.emailAddress && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Email</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Email"
                            disabled
                            defaultValue={approval.emailAddress.value}
                            onChange={(event) => onValueChangedPreQual(approval.emailAddress, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.dob && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Date of Birth</Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                disabled
                                fullWidth
                                inputVariant="outlined"
                                aria-label="Date of Birth"
                                format="MM/dd/yyyy"
                                value={formattedDateOfBirth || null}
                                onChange={(date) => setDateOfBirth(date)}
                                placeholder="mm/dd/yyyy"
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.dob && (
                    <Grid item xs={4}>
                        <Typography className={classes.label} style={{marginLeft: 8}}>
                            Age
                        </Typography>
                        <TextField
                            variant="outlined"
                            aria-label="Age"
                            className={classes.ageField}
                            disabled
                            defaultValue={ageValue}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.gender && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Gender</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                disabled
                                aria-label="Gender"
                                variant="outlined"
                                value={prequal.gender.value}
                                onChange={(event) => onValueChangedPreQual(prequal.gender, event.target.value)}
                                error={!prequal.gender.value}
                                placeholder="Gender"
                            >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.state && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>State</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                disabled
                                aria-label="State"
                                variant="outlined"
                                value={prequal.state.value}
                                onChange={(event) => onValueChangedPreQual(prequal.state, event.target.value)}
                                error={!prequal.state.value}
                                placeholder="State"
                            >
                                {STATE_OPTIONS.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.maritalStatus && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Marital Status</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                disabled
                                aria-label="Marital Status"
                                variant="outlined"
                                value={prequal.maritalStatus.value}
                                onChange={(event) => onValueChangedPreQual(prequal.maritalStatus, event.target.value)}
                                error={!prequal.maritalStatus.value}
                                placeholder="Marital Status"
                            >
                                {fieldOptions.maritalStatus.options.map((status) => (
                                    <MenuItem value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.address && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Street Address</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Street Address"
                            disabled
                            value={approval.address.value}
                            onChange={(event) => onValueChangedPreQual(approval.address, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.addressCity && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>City</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="City"
                            disabled
                            value={approval.addressCity.value}
                            onChange={(event) => onValueChangedPreQual(approval.addressCity, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.addressZipCode && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Zipcode</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Zipcode"
                            disabled
                            value={approval.addressZipCode.value}
                            onChange={(event) => onValueChangedPreQual(approval.addressZipCode, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.birthCountry && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Birth Country</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Birth Country"
                            disabled
                            value={approval.birthCountry.value}
                            onChange={(event) => onValueChangedPreQual(approval.birthCountry, event.target.value)}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.birthState && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Birth State</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                disabled
                                aria-label="Birth State"
                                variant="outlined"
                                defaultValue={approval.birthState.value}
                                onChange={(event) => onValueChangedPreQual(approval.birthState, event.target.value)}
                                placeholder="Birth State"
                            >
                                {STATE_OPTIONS.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.usCitizenship && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>U.S Citizenship</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                aria-label="U.S Citizenship"
                                disabled
                                variant="outlined"
                                defaultValue={approval.usCitizenship.value}
                                onChange={(event) => onValueChangedPreQual(approval.usCitizenship, event.target.value)}
                                placeholder="U.S Citizenship"
                            >
                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                <MenuItem value={'PermanentResident'}>Permanent Resident</MenuItem>
                                <MenuItem value={'USVisaHolder'}>U.S. Visa Holder</MenuItem>
                                <MenuItem value={'NoUSVisaTINonly'}>No U.S. Visa - TIN only</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.validDriversLicense && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Driver's License Status</Typography>
                        <FormControl fullWidth variant="outlined">
                            <Select
                                disabled
                                aria-label="Driver's License Status"
                                variant="outlined"
                                defaultValue={approval.validDriversLicense.value}
                                onChange={(event) =>
                                    onValueChangedPreQual(approval.validDriversLicense, event.target.value)
                                }
                                placeholder="Driver's License Status"
                            >
                                <MenuItem value={'Yes'}>Yes</MenuItem>
                                <MenuItem value={'StateID'}>State ID</MenuItem>
                                <MenuItem value={'NoIDApplicantisaMinor'}>No ID - Applicant is a Minor</MenuItem>
                                <MenuItem value={'NoID'}>No ID</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.height && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Body Height (ft/in)</Typography>
                        <TextField
                            fullWidth
                            disabled
                            aria-label="Body Height (ft/in)"
                            variant="outlined"
                            defaultValue={`${Math.floor(prequal.height.value / 12)}/${prequal.height.value % 12}`}
                            onChange={(event) => handleHeightChange(event.target.value)}
                            error={!prequal.height.value}
                            placeholder="ft/in"
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.weight && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Body Weight (lbs)</Typography>
                        <TextField
                            fullWidth
                            disabled
                            aria-label="Body Weight (lbs)"
                            variant="outlined"
                            defaultValue={prequal.weight.value}
                            onChange={(event) => onValueChangedPreQual(prequal.weight, event.target.value)}
                            error={!prequal.weight.value}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.socialSecurityNumber && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>SSN</Typography>
                        <InputSsn
                            aria-label="SSN"
                            disabled
                            value={approval.socialSecurityNumber.value}
                            onChange={(value: string) => onValueChangedPreQual(approval.socialSecurityNumber, value)}
                        />
                    </Grid>
                )}
            </>
        );
    }

    function renderFinances(renderMissingValueFields = false) {
        return (
            <>
                {renderMissingValueFields === isMissingValueField.nameOfEmployer && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Employer Name</Typography>
                        <TextField
                            fullWidth
                            disabled
                            aria-label="Employer Name"
                            variant="outlined"
                            defaultValue={prequal.nameOfEmployer.value}
                            onChange={(event) => onValueChangedPreQual(prequal.nameOfEmployer, event.target.value)}
                            error={!prequal.nameOfEmployer.value}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.occupation && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Occupation</Typography>
                        <Autocomplete
                            {...(AGENIUM_OCUPATION_LIST.includes(prequal.occupation.value) && {disabled: true})}
                            clearOnBlur
                            disableClearable
                            options={AGENIUM_OCUPATION_LIST}
                            defaultValue={
                                AGENIUM_OCUPATION_LIST.includes(prequal.occupation.value)
                                    ? prequal.occupation.value
                                    : null
                            }
                            onChange={(_, newValue) => onValueChangedPreQual(prequal.occupation, newValue)}
                            filterOptions={(options, state) => {
                                const results = options.filter((v) =>
                                    v.toLowerCase().includes(state.inputValue.toLowerCase()),
                                );
                                return results.length ? results : ['Other Occupation'];
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    aria-label="Occupation"
                                    variant="outlined"
                                    error={!prequal.occupation.value}
                                />
                            )}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.incomeHouseholdText && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Household Income Actual Earning</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            aria-label="Household Income Actual Earning"
                            disabled
                            defaultValue={prequal.incomeHouseholdText.value}
                            onChange={(event) => onValueChangedPreQual(prequal.incomeHouseholdText, event.target.value)}
                            error={!prequal.incomeHouseholdText.value}
                        />
                    </Grid>
                )}
                {renderMissingValueFields === isMissingValueField.existingCoverageAmount && (
                    <Grid item xs={4}>
                        <Typography className={classes.label}>Coverage Amount</Typography>
                        <TextField
                            fullWidth
                            aria-label="Coverage Amount"
                            disabled
                            variant="outlined"
                            defaultValue={prequal.existingCoverageAmount.value}
                            onChange={(event) =>
                                onValueChangedPreQual(prequal.existingCoverageAmount, event.target.value)
                            }
                            error={!prequal.existingCoverageAmount.value}
                        />
                    </Grid>
                )}
            </>
        );
    }

    return (
        <Grid container classes={{root: classes.wrapper}}>
            <Grid item xs={12}>
                <Typography classes={{root: classes.title}}>Does the prequalification info look correct?</Typography>
            </Grid>
            <FormSection title="Basics">
                <Grid container spacing={3}>
                    {renderBasics()}
                </Grid>
            </FormSection>
            <FormSection title="Finances">
                <Grid container spacing={3}>
                    {renderFinances()}
                </Grid>
            </FormSection>
            <Accordion
                classes={{root: classes.uncollectedAccordion}}
                TransitionProps={{unmountOnExit: true}}
                defaultExpanded={false}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <FormSection title="Uncollected Data (Empty)" greyout={true}></FormSection>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {renderBasics(true)}
                        {renderFinances(true)}
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <FixedNavigation isNextDisabled={!formattedDateOfBirth} onNext={onNext} isLoading={false} />
        </Grid>
    );
});

export default Review;
